/**
 * salesPlayConfigs.js
 * Definitions for the Sales Studio plays that run on the Image Lab engine.
 * Every play captures the recipient's live website (brand reference) and
 * renders up to `maxItems` images per contact, one {{image:token}} each.
 *
 * Item `aspect` only applies when the play sets `perItemAspect`; otherwise the
 * play-level aspect is used for every item.
 */

const BRAND_STUDY =
  "Before you design anything, study the business: the attached screenshots of their current website, their logo, "
  + "brand colors, typography, imagery style and tone of voice, plus the recipient information below (industry, "
  + "location, offer, research).";

// Page templates for the Website Redesigner, per page type. Picking one copies
// its `text` into the page prompt. Example images live at
// `${exampleBase}/looks/<page type>/<template>.webp` (scripts/gen-website-looks.js).
const look = (id, label, light, text) => ({ id, label, light, text });
const WEBSITE_LOOKS = {
  home: [
    look("modern", "Modern & clear", "Big photo, one button",
      "Modern and clear: a 2026 homepage that feels light and confident. Top: a slim header with their logo on the left, four or five short menu items from their real navigation and one small button on the right. "
      + "Below it a large hero with a real-looking photo of their place, product or work filling at least half the screen, a short headline of five to eight words in their language, one supporting sentence and one primary button in their brand color. "
      + "Then a calm row of three cards with what they offer, each with a photo, a two-word title and one line of text. "
      + "Plenty of white space, a clean sans-serif or their own font, consistent 8 px spacing, soft rounded corners, sharp and readable text. Same character and price level as their current site, just clearly better."),
    look("premium", "Premium & minimal", "Calm, refined",
      "Premium and minimal: a homepage that feels like a high-end brand. A very quiet header, their logo centered or on the left, a few menu items in small spaced capitals. "
      + "A calm, full-width hero photo with soft natural light and muted colors, a short headline in an elegant serif set large, and one understated text button or thin outlined button. "
      + "Below, one short paragraph about what makes them special, then two or three large photos in an asymmetric grid with small captions. "
      + "Very little color: their brand color only as a fine accent, warm off-white or their own background, generous margins, thin lines instead of boxes. High-end without becoming a different brand."),
    look("bold", "Bold full-bleed", "Strong contrast",
      "Bold: a full-bleed, high-contrast hero photo covering the whole first screen, with a dark gradient at the bottom so the text stays readable. "
      + "A large headline of three to six words in heavy type across the lower left, one short subline and one strong button in their brand color. "
      + "A minimal transparent navigation over the photo with their logo in white or their own version. "
      + "Below the fold, a contrasting band in their brand color with three short facts or benefits in big type, then a row of photos. Confident and energetic, but still clearly their logo, their colors and their real offer."),
    look("local", "Place and trust", "Where they are",
      "Place and trust: a hero built around their real place, the building, the street, the view or the room people actually come to, with a warm headline that names the town or area. "
      + "Directly below, a slim info strip with the address, opening hours and phone, only what is known. "
      + "Then a section with the reasons people already trust them, only real ones from their site (years in business, family run, regional products, a real certificate), each with a small icon. "
      + "Finish with a small map preview and one button to visit, call or book. Warm natural photos, friendly type, their colors. No stock skyline, no generic city photo."),
    look("offer", "Offer first", "Lead with the offer",
      "Offer first: the homepage leads with their real main offer or package as the hero, with a large photo of exactly that offer. "
      + "A clear headline that names the offer, three true benefits as short lines with check icons, and one prominent button such as book, order or enquire. "
      + "Below, a short section on who it is for and how it works in three small steps, then a second, quieter button. "
      + "Strong visual hierarchy: the offer is the first thing the eye lands on, everything else supports it. A price only if it is on their site, no invented discount, no countdown timer."),
    look("editorial", "Editorial", "Magazine look",
      "Editorial: a magazine-style homepage. A slim header, then a large elegant serif headline that reads like a magazine cover line, with a short intro paragraph beside it. "
      + "An asymmetric photo layout: one tall portrait photo next to one wide landscape photo, overlapping slightly, with small captions in italic. "
      + "Short text blocks in two columns, pull quotes in large type (only words they actually use), thin rules between sections and plenty of white space. "
      + "Refined colors: off-white background, dark text, their brand color as a sparing accent. It should feel curated and calm, like a feature story about them."),
  ],
  booking: [
    look("calendar", "Calendar + slots", "Days and times",
      "A booking page with an integrated calendar tool. Their usual header with logo on top, then a short headline such as 'Termin buchen' in their language. "
      + "On the left, a clean month view with available days highlighted in their brand color and past days greyed out; on the right, the time slots for the selected day as rounded buttons, one of them selected. "
      + "Below, a short booking form with labeled fields for name, email, phone and a note, and one large confirm button. A small summary of the chosen date and time above the button. "
      + "Calm, app-like interface, clear labels, generous spacing, everything readable. What can be booked comes from their site."),
    look("short", "Short form", "Few fields",
      "A simple booking page that feels effortless. Their header with logo, then one centered card on a calm background. "
      + "Inside the card: a short title naming what is being booked, a date field with a small calendar icon, a time or guest-count select, name and email fields, and one wide confirm button in their brand color. "
      + "A small line under the button with what happens next, for example a confirmation by email. "
      + "No extra steps, no upsells, no clutter. Large tap targets, clear labels above the fields, a friendly tone in their language."),
    look("cards", "Pick, then book", "Options as cards",
      "Booking as cards: a page that first shows what can be booked, rooms, tables, treatments or appointments, as a grid of two or three cards. "
      + "Each card has a real-looking photo, a short name, two or three facts with small icons (size, duration, capacity) and its own book button. "
      + "One card is selected and below the grid a short booking form opens with date, guests and contact fields and a confirm button. "
      + "Prices only if they publish them. Consistent card heights, clear hierarchy, their colors and logo."),
    look("split", "Photo + form", "Split screen",
      "A split booking page: the left half is one large real-looking photo of their place or service, edge to edge, with their logo small in a corner. "
      + "The right half is calm and light: a short headline, one sentence of reassurance, and a compact form with date, time or guests, name, email and one confirm button. "
      + "Optionally a small line with phone and opening hours under the form, only if known. "
      + "Balanced and elegant, generous padding, their brand color only on the button and small accents."),
  ],
  services: [
    look("cards", "Service cards", "One card each",
      "A services overview page. A short intro headline and one sentence about how they help, then a clean grid of three to six cards, one per real service from their site. "
      + "Each card: a fitting photo or a simple line icon, the service name, one short benefit sentence and a small 'Mehr erfahren' style link or button. "
      + "A starting price only if it is on their site. Below the grid, a calm band with one call to action to get in touch. "
      + "Equal card sizes, clear spacing, readable text, their logo, colors and typography."),
    look("one", "One service", "Their main one",
      "A page about their single most important service. A hero with a large photo of that service in action and a headline that names the outcome for the customer. "
      + "Then four clear sections: what it is, who it is for, how it works in three short steps, and what the customer gets, each with a short paragraph and a photo or icon. "
      + "One primary button in the hero and the same button again at the end. "
      + "No invented packages, numbers or testimonials. Clean structure, their colors and type."),
    look("steps", "How it works", "Three steps",
      "A services page that explains in three clear steps how working with them goes. A short headline, then three large numbered steps side by side, each with a big number, a short title, one or two sentences and a fitting photo or icon, connected by a subtle line. "
      + "Below, a short section with the services themselves as a simple list, then one button to start. "
      + "Easy to scan in five seconds, their brand color for the numbers and the button, plenty of white space."),
  ],
  shop: [
    look("grid", "Product grid", "Their real range",
      "A shop page: a short category headline, a slim filter bar with a few real categories as pills, and a clean grid of six to nine products from their real range. "
      + "Each product tile: a consistent product photo on a light background, the product name, a price only if they publish it, and a small add or enquire button. "
      + "Uniform photo sizes and spacing, a calm header with their logo and a cart or enquiry icon. "
      + "It should feel like a modern, trustworthy shop in their own brand, not a generic marketplace."),
    look("featured", "Featured product", "One hero product",
      "A shop page led by one featured product from their real range: a large product photo on the left, on the right the product name, a short description, two or three real details as bullet points, and a clear buy or enquire button. "
      + "Below, a row of more products from their range as smaller tiles. "
      + "Prices only if they publish them. Calm studio-like product photography, their colors and logo, clear hierarchy."),
    look("lookbook", "Lookbook", "Photos sell",
      "A lookbook-style shop page: large, atmospheric photos of their real products in use, in a staggered full-width layout with very little text. "
      + "Each photo carries a small label with the product name and a discreet link to the product. "
      + "Between the photos one short line of copy in an elegant font. Prices only if they publish them. "
      + "It should feel like a seasonal campaign, with their colors, logo and real products."),
  ],
  about: [
    look("story", "Their story", "No stock team",
      "An about-us page that tells their real story. A hero with a photo of their place or work and a headline about what drives them. "
      + "Then the story in two or three short chapters, each with a photo: how it started, what they do today, what matters to them. Only facts from their site or the provided information. "
      + "People appear only if they are known; otherwise show the place, hands at work and details. "
      + "Warm, personal tone in their language. No stock portraits, no invented headcount or founding year."),
    look("place", "Place and people", "Real place first",
      "An about page built around their real place: a large, wide photo of the location as the hero, then a grid of smaller photos of rooms, details and, if known, the people. "
      + "A short, honest text block beside the grid about what guests or customers find here. "
      + "Generous photos, little text, natural light, their colors and logo. No stock portraits."),
    look("values", "What they stand for", "Three values",
      "An about page with three short values they can actually claim, based on their site. A short intro headline, then three large blocks, each with a real-looking photo, a one-word or two-word value and one sentence that proves it. "
      + "Close with one line about inviting the visitor in and one button. "
      + "Clean, confident layout, their brand color for the value words, no invented awards or claims."),
  ],
  contact: [
    look("reach", "Map and form", "Form and place",
      "A contact page in two columns. On the left, a short headline, a friendly sentence and a compact form with name, email, message and a send button. "
      + "On the right, a map preview of their real location with a pin, and below it the address, opening hours, phone and email with small icons. "
      + "Leave out anything that is not known. Clean, calm, their colors and logo, everything clearly readable."),
    look("card", "Simple card", "Phone, mail, hours",
      "A minimal contact page: their header, then one calm centered card on a soft background with phone, email, address and opening hours, each on its own line with a small icon. "
      + "A small map preview under the card. No long form, just one button to call or write. "
      + "Generous spacing, large readable text, their brand color as the only accent."),
  ],
  landing: [
    look("oneGoal", "One goal", "Headline to button",
      "A focused campaign landing page for their real main offer. No full navigation, only the logo. "
      + "Hero: a strong photo of the offer, a headline that states the benefit, one supporting sentence and one big button. "
      + "Then three true benefits with icons, a section with proof they actually have (real certificates, years, a real quote only if it is on their site), and the same button again at the end. "
      + "Everything leads to that one action. No invented FAQ answers, numbers or testimonials."),
    look("event", "Event page", "Date and signup",
      "A landing page for a real event or seasonal offer. Hero with a large atmospheric photo, the event title in big type, and date and place in a clear badge, only if they are known. "
      + "Below, a short description, a small schedule or what is included as a list, and one signup or booking button. "
      + "A festive but on-brand look in their colors. Leave a detail out rather than inventing it."),
  ],
};

const WEBSITE_THEME =
  "Look at everything you know about the prospect first: their website, logo, branding and the style they go for. "
  + "Then make it a lot more modern and better, so they instantly see how good their site could look.";

const WEBSITE_REDESIGN = {
  id: "websiteRedesign",
  name: "Website Redesigner",
  defaultName: "Website Redesign",
  defaultPrefix: "redesign",
  itemNoun: "Page",
  itemNounPlural: "Pages",
  itemsHint:
    "Per page: pick what it is, then a template. The template fills the prompt, which you can edit freely.",
  maxItems: 3,
  smartPieces: true,
  lookShots: true,
  preview: "site",
  engineHint: "Same homepage, three businesses. A high text score keeps menus and buttons readable. Brand match keeps it their site.",
  exampleNote: "old homepage, so you can see who keeps the layout and the words, and who just makes a pretty picture.",
  outputSub: "Name, tag, and a live test of the email for a real contact.",
  demoText: "I sketched how your website could look with a fresh design. Here is the homepage:",
  smartCopy: {
    kicker: "The page",
    question: "Which page?",
    group: "Which page",
    ideaKicker: "The template",
    ideaQuestion: "How should it look?",
    promptLabel: "Prompt",
    promptNote: "from the template, edit it freely",
    hint: "The redesign always keeps their real logo, name and colors, and never invents prices or claims.",
  },
  perItemAspect: false,
  defaultAspect: "landscape",
  defaultModel: "gemini-3-pro-image",
  modelFocus: ["brand", "text", "edit"],
  modelFocusLabel: "brand match, text and staying true to their site",
  defaultItems: ["home"],
  defaultDirection: "",
  directionPlaceholder: "",
  itemTypes: [
    { id: "home", label: "Homepage", icon: "fa-house", directions: WEBSITE_LOOKS.home },
    { id: "booking", label: "Booking / Calendar", icon: "fa-calendar-check", directions: WEBSITE_LOOKS.booking },
    { id: "services", label: "Services", icon: "fa-briefcase", directions: WEBSITE_LOOKS.services },
    { id: "shop", label: "Shop / Product", icon: "fa-bag-shopping", directions: WEBSITE_LOOKS.shop },
    { id: "about", label: "About us", icon: "fa-users", directions: WEBSITE_LOOKS.about },
    { id: "contact", label: "Contact", icon: "fa-envelope", directions: WEBSITE_LOOKS.contact },
    { id: "landing", label: "Campaign landing page", icon: "fa-bullseye", directions: WEBSITE_LOOKS.landing },
    { id: "custom", label: "Custom page", icon: "fa-pen", brief: "" },
  ],
  modelFit: {
    "gemini-3-pro-image": "Best overall: their brand stays, and menus, buttons and headlines read correctly.",
    "gpt-image-2": "Best when the page is full of labels, prices and buttons.",
    "flux-2-max": "The best-looking page. Small interface text is a little freer.",
    "flux-2-pro": "A solid redesign. Fine print is less reliable.",
    "gemini-3.1-flash-lite-image": "Fast drafts. Layout and labels drift more.",
    "gpt-image-1-mini": "Only for a rough first look. It tends to reinvent the page.",
  },
  exampleBase: "/img/sales-studio/website",
  exampleBefore: true,
  examples: {
    scenarios: [
      { id: "hotel", label: "Hotel", type: "home", engineDirection: "modern" },
      { id: "restaurant", label: "Restaurant", type: "home", engineDirection: "modern" },
      { id: "realEstate", label: "Real estate", type: "home", engineDirection: "modern" },
    ],
  },
  styles: [],
  frames: [
    { id: "desktop", label: "Desktop browser", prompt: "Show the page inside a realistic desktop browser window mockup on a soft neutral background." },
    { id: "devices", label: "Desktop + mobile", prompt: "Show the page in a realistic desktop browser window, with the matching smartphone version overlapping in the front right corner." },
    { id: "flat", label: "Flat page, no frame", prompt: "Render the page design flat and edge-to-edge, without any device or browser frame." },
  ],
  buildPrompt: ({ label, brief, frame }) => [
    `Design a redesign concept of the "${label}" page for {{business_name}}.`,
    `${BRAND_STUDY} The result must be unmistakably THEIR brand: keep the exact logo, company name and brand colors.`,
    `Overall direction: ${WEBSITE_THEME}`,
    `This page: ${brief}`,
    "Use realistic copy in the language of their current website. No lorem ipsum, no placeholder buttons, no fake logos.",
    "Do not invent prices, reviews, awards, team members or navigation items that are not on their site. A traditional business does not become a tech startup.",
    `Presentation: ${frame.prompt}`,
  ],
  altText: (label) => `${label} redesign concept for {{business_name}}`,
};

// Visual templates for the Content Creator. Each channel lists the looks that
// suit it; picking one copies its `text` into the piece prompt, which the user
// can then edit. Example images live at `${exampleBase}/looks/<channel>/<look>.webp`
// (scripts/gen-content-looks.js).
const CONTENT_LOOKS = {
  onBrand: {
    label: "Exactly on brand",
    light: "Like their own team",
    text:
      "Exactly on brand: a piece that looks as if their in-house marketing team made it. Use the photo mood of their own website (same kind of light, colors and subjects), their brand colors for text and accents, and a font that matches their typography. "
      + "One real-looking photo of what they offer as the base, a short headline of three to seven words placed where the photo has calm space, an optional one-line subline, and their logo small and clean in a corner. "
      + "Clear hierarchy, consistent margins, nothing that feels like a template. It should be impossible to tell it apart from their own posts.",
  },
  photoFirst: {
    label: "Photo first",
    light: "One line of text",
    text:
      "Photo first: one strong, real-looking photo of what they are proud of fills the whole frame, their signature product, room, view or work. "
      + "Shot like a professional photographer would: natural light, a clear subject, a calm background, true colors and real textures. "
      + "One short line of text, at most six words, set small and elegant in a corner where the photo is quiet, and their logo small in the opposite corner. Nothing else on the image.",
  },
  scrollStopper: {
    label: "Scroll-stopping",
    light: "Bold, high contrast",
    text:
      "Scroll-stopping: a bold, close crop of their product or place with strong contrast and saturated but true colors, so it pops in a busy feed. "
      + "A big headline of three to five words in heavy type that reads on a phone at a glance, set on a solid block or band in their brand color for maximum contrast. "
      + "Dynamic composition with the subject slightly off center, their logo small but visible. Energetic, confident, still clearly their brand.",
  },
  editorial: {
    label: "Premium editorial",
    light: "Magazine, calm",
    text:
      "Premium editorial: a layout like a page from a high-end magazine. One refined photo with soft natural light takes about two thirds of the frame, the rest is calm off-white or their background color. "
      + "An elegant serif headline set large, one short line of supporting text in a small clean sans-serif, thin rules and generous white space. "
      + "Their brand color only as a fine accent, their logo small and discreet. Calm, curated and expensive-looking.",
  },
  collage: {
    label: "Photo grid",
    light: "Four real photos",
    text:
      "Photo grid: three or four real-looking photos in a clean grid with thin even gaps, one larger hero photo and smaller detail shots, for example the place, the signature product, a detail and a moment of use. "
      + "All photos share the same light and color mood so they feel like one shoot. "
      + "A short headline on a solid bar in their brand color across the grid, and their logo small on the bar. Tidy, balanced and inviting.",
  },
  statement: {
    label: "Statement card",
    light: "Words, no photo",
    text:
      "Statement card: no photo. One short sentence in their own voice, something they stand for or would really say, set large and centered on a calm solid background in their brand color or a soft tint of it. "
      + "Strong typography is the whole design: a confident font with good line spacing, at most three lines, perfect kerning. "
      + "Their logo small at the bottom, lots of breathing room around the text. Never a fake customer review or invented quote.",
  },
  seasonal: {
    label: "Seasonal",
    light: "Fits the send date",
    text:
      "Seasonal: their place or product in the season of the send date, with natural light and colors that belong to that season (low warm sun in autumn, crisp light and snow in winter, fresh green in spring, bright light in summer). "
      + "At most one fitting seasonal detail that belongs to this business, nothing more. "
      + "A short seasonal headline of three to six words in their brand colors and their logo small in a corner. Warm and inviting. No kitsch, no holiday props, no generic decorations.",
  },
  typePoster: {
    label: "Big type",
    light: "The headline is the design",
    text:
      "Big type: the headline is the design. Huge bold letters, three to five words, run across the frame edge to edge, in their brand colors on a contrasting background. "
      + "Play with scale: one key word much larger than the rest, tight leading, words stacked over several lines. "
      + "Optionally a small cropped photo peeking through or placed in one corner. Their logo small at the bottom. Graphic, modern and readable on a phone.",
  },
  checklist: {
    label: "Three benefits",
    light: "Check icons",
    text:
      "Three benefits: a clear headline at the top, then three short, true benefits stacked below, each on its own line with a simple check icon in their brand color and at most six words. "
      + "A small real-looking photo of their offer on one side or as a soft background, so the text stays the focus. "
      + "Their logo small at the bottom, clean alignment, plenty of space between the lines. Only benefits that come from their site, no invented numbers or claims.",
  },
  hookStory: {
    label: "Full-bleed hook",
    light: "Hook and sticker",
    text:
      "Full-bleed hook: one vertical photo of their place or product fills the whole screen, shot like a real phone video still, natural and close. "
      + "A short hook of four to eight words at the top third in bold type with a soft shadow or band so it reads on any photo. "
      + "One call-to-action sticker as a rounded pill near the bottom third, for example 'Jetzt buchen' in their language. "
      + "Keep the very top and bottom free for the app's own buttons. Their logo small, native to stories, not an ad.",
  },
  ugc: {
    label: "Phone snapshot",
    light: "Real, not an ad",
    text:
      "Phone snapshot: it looks like a real guest or customer took it on a phone, a slightly imperfect angle, natural available light, casual framing, real everyday details around the subject. "
      + "The subject is their real product, dish, room or place in a happy, genuine moment. "
      + "One short handwritten-style line or a simple caption sticker, nothing else, no logo or only a tiny one. Authentic and believable, never a polished ad.",
  },
  event: {
    label: "Event poster",
    light: "Title, date, place",
    text:
      "Event poster: a big event or offer title as the hero, set in bold type in their brand colors, with the date and place in a clear secondary block, only if they are in the provided information. "
      + "One atmospheric hero photo that fits the event, a clear hierarchy from title to details to a short call to action, and their logo at the bottom. "
      + "Festive but on brand. Leave a detail out rather than inventing it.",
  },
  offerHero: {
    label: "Offer + button",
    light: "Only real offers",
    text:
      "Offer + button: their real main offer is the hero, shown in a large real-looking photo of exactly that product, room or service. "
      + "A clear benefit headline of four to eight words, one short supporting line, and one call-to-action button shape in their brand color with a short verb such as book, order or try. "
      + "Their logo in a corner, the button clearly the strongest color on the image. A price or discount only if it is in the provided information.",
  },
  behindScenes: {
    label: "Behind the scenes",
    light: "Hands at work",
    text:
      "Behind the scenes: hands at work in their kitchen, workshop, room or office, doing what they really do, with tools and materials that fit their craft. "
      + "Warm natural light, unposed and close, real textures, a little depth of field. "
      + "One short line about how they work, set small on a calm part of the photo, and their logo small. Real people only if known, otherwise hands and details only.",
  },
  voucher: {
    label: "Elegant voucher",
    light: "Title and code field",
    text:
      "Elegant voucher: a refined card in their brand colors that looks good printed and on screen. Their real offer as the title in an elegant font, one short description line, an empty code field with a thin outline, and their logo. "
      + "A subtle frame or fine pattern from their brand, generous margins, a calm premium look. "
      + "No invented amount, expiry date or discount.",
  },
  giftCard: {
    label: "Gift card",
    light: "Space for an amount",
    text:
      "Gift card: calm and branded, like a high-quality physical card. Their name and logo, one warm line in their language such as a gift wish, and an empty, clearly marked space for an amount the sender fills in. "
      + "Soft background in their brand color or a tint of it, a fine border, an elegant font. Never print a price.",
  },
};
const looks = (...ids) => ids.map((id) => ({ id, ...CONTENT_LOOKS[id] }));

const CONTENT_THEME =
  "Find what this business is proud of (signature product, rooms, location, team or current offer) and turn it into content "
  + "they would happily post themselves tomorrow.";

const CONTENT_PIECES = {
  id: "contentPieces",
  name: "Content Creator",
  defaultName: "Content Pieces",
  defaultPrefix: "content",
  itemNoun: "Piece",
  itemNounPlural: "Content pieces",
  itemsHint:
    "Pick the channel, then a look. The look fills the prompt, which you can edit freely. The format follows the channel.",
  maxItems: 3,
  smartPieces: true,
  lookShots: true,
  outputSub: "Name, tag, and a live test of the email for a real contact.",
  demoText: "I put a post together for you, in your own look and with your own offer:",
  smartCopy: {
    ideaKicker: "The look",
    ideaQuestion: "How should it look?",
    promptLabel: "Prompt",
    promptNote: "from the look, edit it freely",
    hint: "Their real logo, colors and offer always stay. No invented prices, dates or reviews.",
  },
  perItemAspect: true,
  defaultAspect: "square",
  defaultModel: "gemini-3-pro-image",
  modelFocus: ["brand", "text", "photo"],
  modelFocusLabel: "brand match, text on the visual and photo look",
  defaultItems: ["instagramPost"],
  defaultDirection: "",
  directionPlaceholder: "",
  itemTypes: [
    {
      id: "instagramPost",
      label: "Instagram post",
      icon: "fa-instagram",
      iconStyle: "fa-brands",
      aspect: "square",
      format: "A square Instagram feed post.",
      directions: looks("onBrand", "photoFirst", "scrollStopper", "editorial", "collage", "statement", "seasonal"),
    },
    {
      id: "instagramStory",
      label: "Instagram story",
      icon: "fa-mobile-screen",
      aspect: "portrait",
      format:
        "A vertical Instagram / TikTok story. Keep text and stickers away from the top and bottom edges, where the app shows its own buttons.",
      directions: looks("hookStory", "ugc", "event", "behindScenes", "seasonal"),
    },
    {
      id: "carousel",
      label: "Carousel cover",
      icon: "fa-clone",
      aspect: "square",
      format:
        "The first slide of an Instagram / LinkedIn carousel. It has to make people swipe, with a hint of the next slide at the right edge.",
      directions: looks("typePoster", "checklist", "editorial"),
    },
    {
      id: "metaAd",
      label: "Facebook / Meta ad",
      icon: "fa-facebook",
      iconStyle: "fa-brands",
      aspect: "square",
      format: "A square paid social ad for Facebook and Instagram, readable on a phone at a glance, with one clear call to action.",
      directions: looks("offerHero", "ugc", "checklist", "scrollStopper"),
    },
    {
      id: "displayAd",
      label: "Google display ad",
      icon: "fa-rectangle-ad",
      aspect: "landscape",
      format: "A landscape Google display banner: logo, short headline and one button, readable at small sizes.",
      directions: looks("offerHero", "onBrand", "typePoster"),
    },
    {
      id: "linkedinPost",
      label: "LinkedIn post",
      icon: "fa-linkedin",
      iconStyle: "fa-brands",
      aspect: "landscape",
      format: "A landscape LinkedIn feed visual: professional, calm, one clear message. No stock handshakes or stock colleagues.",
      directions: looks("statement", "behindScenes", "checklist", "editorial"),
    },
    {
      id: "flyer",
      label: "Flyer / Poster",
      icon: "fa-file-image",
      aspect: "portrait",
      format: "A portrait print flyer or poster with a clear hierarchy and their contact details, readable from a distance.",
      directions: looks("event", "typePoster", "editorial", "seasonal"),
    },
    {
      id: "offerCard",
      label: "Offer / Voucher",
      icon: "fa-ticket",
      aspect: "landscape",
      format: "A landscape voucher or offer card that works printed and sent digitally.",
      directions: looks("voucher", "giftCard"),
    },
    { id: "custom", label: "Custom piece", icon: "fa-pen", aspect: "square", brief: "" },
  ],
  modelFit: {
    "gemini-3-pro-image": "Best overall: their logo, their colors and a headline you can read.",
    "flux-2-max": "Best when the photo is the post and the text is one short line.",
    "gpt-image-2": "Best for ads, vouchers and posters, where the words do the work.",
    "flux-2-pro": "Strong photo, weaker small text. Good for simple posts.",
    "gemini-3.1-flash-lite-image": "Fast drafts. Headlines and logos are less reliable.",
    "gpt-image-1-mini": "Only for very simple layouts with little text.",
  },
  exampleBase: "/img/sales-studio/content",
  exampleRatio: "1 / 1",
  exampleBefore: true,
  exampleNote:
    "post, made from their logo file, the colors read from their site and one real photo from their website. Hover for what the engine got. Check who keeps the logo and turns the real photo into a better one.",
  examples: {
    scenarios: [
      { id: "hotel", label: "Hotel", type: "instagramPost", engineDirection: "signature" },
      { id: "restaurant", label: "Restaurant", type: "instagramPost", engineDirection: "signature" },
      { id: "realEstate", label: "Real estate", type: "instagramPost", engineDirection: "signature" },
    ],
  },
  styles: [],
  frames: [
    { id: "asset", label: "Clean asset only", prompt: "Render only the finished content piece itself, edge-to-edge, ready to post." },
    { id: "phone", label: "In a phone mockup", prompt: "Show the content piece as it would appear in the app feed on a realistic smartphone, held or placed on a clean background." },
    { id: "feed", label: "Feed preview with profile", prompt: "Show it as a native post preview of their own account: profile picture with their logo, account name, the visual, likes and a short caption." },
  ],
  buildPrompt: ({ label, brief, format, frame }) => [
    `Create a "${label}" content piece for {{business_name}}.`,
    format ? `Format: ${format}` : "",
    `${BRAND_STUDY} It must look like content their own marketing would publish: use the exact logo, company name and brand colors, and pick visuals that fit what they really offer.`,
    `What to show: ${CONTENT_THEME}`,
    `Look and content: ${brief}`,
    "Text on the image is short, spelled correctly and in the language of their website. No lorem ipsum, no placeholder copy, no fake buttons that say nothing.",
    "Do not invent prices, discounts, dates, star ratings, awards, review counts or claims that are not in the provided information. Leave it out instead.",
    "No generic stock scenes (handshakes, random smiling teams, floating product pedestals) and no trend styling that is not theirs. A traditional business stays traditional.",
    /season/i.test(brief) ? "The current season is {{current_season}}." : "",
    `Presentation: ${frame.prompt}`,
  ],
  altText: (label) => `${label} for {{business_name}}`,
};

// Photo Upgrade: per contact the backend crawls the site, a vision model
// picks the photo matching `target`, and only that photo is edited. Item
// `pageHints` steer which subpages are crawled; `target` is what the photo
// must show; `brief` is what we do with it.
// Example images are generated by scripts/gen-photo-upgrade-examples.js from
// AI "before" photos, one folder per scenario.
const PHOTO_EXAMPLES = "/img/sales-studio/photo-upgrade";
export const photoExample = (scenarioId, name) => `${PHOTO_EXAMPLES}/${scenarioId}/${name}.webp`;

// Hard rules against the typical failures of AI photo edits. They apply to
// every Photo Upgrade prompt; the user never sees or edits them.
const PHOTO_RULES = [
  "Realism: the result must be indistinguishable from a real camera photo and never look AI-generated. Keep real " +
    "textures and small natural imperfections (wood grain, fabric creases, carpet, wall and skin texture). No HDR " +
    "look, no oversaturated or teal-and-orange grading, no plastic or overly smooth surfaces, no glow, haze, lens " +
    "flares, fake bokeh or fake steam. Keep the original camera angle, lens and framing unless the direction asks " +
    "for a new shot.",
  "New shots: when the direction asks for a close-up, a new angle or a scene, you may move the camera, crop closer " +
    "and change what is around the subject, but the subject itself is the real one from the photo, rebuilt " +
    "faithfully from what is visible. Shallow depth of field only as a real lens would produce it.",
  "Style: keep the style, era, price level and regional character that is already in the photo (a traditional " +
    "alpine room stays alpine, a classic restaurant stays classic, a budget room stays believable for its price). " +
    "Never impose trend looks such as boho, Japandi, Scandinavian, rattan, macramé, pampas grass, arched mirrors, " +
    "beige-on-beige minimalism or generic Instagram styling unless the direction explicitly asks for that style.",
  "Props: add nothing the direction does not ask for. Never use stock-photo clichés such as a book and a vase on " +
    "the nightstand, rows of candles, scattered petals, a sprig of rosemary on every plate or latte art. If the " +
    "direction allows props, use at most two that genuinely belong to this business.",
  "Fidelity: never change what a customer would actually find. Keep room size and proportions, the real view " +
    "through the windows (same mountains, buildings and landscape; only weather and light may change), bed size " +
    "and count, built-in fixtures (radiators, sockets, switches, doors, lamps, built-ins), floor and wall " +
    "materials, the product's shape, color and label, the dish's ingredients and portion, and people's identity. " +
    "Temporary clutter (cables, chargers, bags, personal items, packaging, trash) is not part of the place: remove " +
    "it whenever the direction asks for a tidy, neat or clutter-free look.",
  "Physics and text: light comes from the real windows and lamps in a consistent direction, every object has a " +
    "contact shadow, reflections match the scene, windows are not blown out, lines are straight without warping. " +
    "Existing signs, labels and logos stay unchanged and legible; add no new readable text (book spines and " +
    "screens stay unreadable).",
];

const shot = (id, label, light, text) => ({ id, label, light, text });

// Direction suggestions per photo type. Picking one copies its `text` into
// the photo's direction field, which the user can then edit freely; only that
// text reaches the model.
const retouch = (fixes) => ({
  id: "natural",
  label: "Clean retouch",
  light: "Same light, corrected",
  text:
    "Retouch it like a professional photographer: correct brightness and white balance, true natural colors, " +
    `straight lines, natural sharpness. ${fixes} Same moment and time of day, add nothing new.`,
});
const seasonal = (touches, fixes = "") => ({
  id: "seasonal",
  label: "Seasonal mood",
  light: "Fits the send date",
  text:
    "Stage it for the season of the send date: show that season outside or in nature where visible, with matching " +
    `natural light, and add at most two or three small, tasteful seasonal touches (${touches}). ${fixes}No kitsch ` +
    "or holiday decoration, same subject and framing.",
});

const PHOTO_UPGRADE = {
  id: "photoUpgrade",
  name: "Photo Retoucher",
  defaultName: "Photo Retoucher",
  defaultPrefix: "photo",
  itemNoun: "Photo",
  itemNounPlural: "Photos",
  itemsHint:
    "Two decisions per photo: what we find on their website, and what should happen to it. If nothing on the site fits, that image is skipped.",
  maxItems: 3,
  perItemAspect: true,
  perItemDirection: true,
  hasTarget: true,
  demoText: "I took a photo from your website and gave it a professional upgrade:",
  sourceMode: "websitePhoto",
  defaultAspect: "landscape",
  defaultModel: "gemini-3-pro-image",
  modelFocus: ["edit", "photo"],
  modelFocusLabel: "keeping the real photo recognizable and photo quality",
  defaultItems: ["hero"],
  studyText:
    "Per contact we crawl their homepage plus up to two matching subpages, collect the real photos and let a vision model pick the one that fits. Logos, icons and tiny images are ignored.",
  studiedInputs: [
    { icon: "fa-images", label: "All real photos on their site" },
    { icon: "fa-sitemap", label: "Matching subpages (e.g. /rooms)" },
    { icon: "fa-eye", label: "AI picks the right photo" },
    { icon: "fa-ban", label: "Skips if nothing fits" },
  ],
  defaultDirection: "",
  directionPlaceholder: "",
  itemTypes: [
    {
      id: "hero",
      guard: "Whatever the photo shows stays the same place, product or people.",
      label: "Main photo",
      icon: "fa-image",
      exampleScenario: "bakery",
      aspect: "landscape",
      target: "the main, most representative photo of the business (e.g. the large hero image at the top of the homepage)",
      pageHints: [],
      directions: [
        retouch("Remove distracting clutter."),
        shot("detail", "Signature close-up", "Their best detail",
          "A new close-up shot of the most characteristic thing in the photo (their product, craft or material): " +
            "the camera moves in close, texture and craftsmanship fill the frame, soft natural light from the side, " +
            "the background falls softly out of focus. Same real thing, nothing invented."),
        shot("inUse", "In the moment", "Hands, real use",
          "The same place or product in a real moment of use: a hand reaching for it, picking it up or serving it, " +
            "natural light, candid and unposed. Only hands and forearms, no faces. Same place and products."),
        seasonal("e.g. autumn leaves, a bit of snow outside, spring blossoms"),
      ],
    },
    {
      id: "interior",
      guard: "Equipment, treatment chairs, shelves and furniture stay the same pieces in the same places.",
      label: "Practice / Salon / Shop / Office",
      icon: "fa-store",
      exampleScenario: "salon",
      aspect: "landscape",
      target: "an interior photo of their practice, salon, studio, shop, office or workshop",
      pageHints: ["praxis", "salon", "studio", "räume", "raeume", "galerie", "gallery", "einblicke", "werkstatt", "laden", "buero", "büro", "office"],
      directions: [
        retouch("Remove cables, paperwork and stray items from the surfaces."),
        shot("ready", "Ready for you", "The guest's view",
          "Shot from where a customer would sit or stand as they arrive: the main chair, bench or counter prepared " +
            "for the next appointment (a fresh folded towel, tidy tools), soft daylight, nobody in the room. Same " +
            "room, furniture and equipment."),
        shot("tools", "Tools of the trade", "Close-up of the craft",
          "A new close-up of the tools and materials of their craft as they really use them in this room, neatly " +
            "laid out on their own work surface, soft side light, the room softly out of focus behind. Only tools " +
            "that fit what is visible in the photo, no brand names."),
        {
          id: "welcome",
          label: "Warm welcome",
          light: "Warm, lamps on",
          text:
            "Give the same room a warm, welcoming late-afternoon mood: the existing lamps switched on, warm light, " +
            "bright enough to see every detail, cables and stray items off the floor and the surfaces. Furniture and " +
            "equipment stay exactly the same, add nothing new.",
        },
        seasonal("e.g. a small seasonal plant or branch in a vase"),
      ],
    },
    {
      id: "product",
      guard: "The product is never redrawn: same proportions, color, material, label text and logo, at a realistic size in the scene.",
      label: "Product",
      icon: "fa-box-open",
      exampleScenario: "product",
      aspect: "square",
      target: "a photo of a single product they sell (packshot or product on a plain background)",
      pageHints: ["shop", "produkte", "products", "product", "kollektion", "collection", "sortiment", "store"],
      directions: [
        {
          id: "packshot",
          label: "Clean packshot",
          light: "Studio light",
          text:
            "Turn it into a clean e-commerce packshot: the exact same product on a seamless light background, soft " +
            "even studio light and a natural contact shadow. Product shape, color, label and details stay identical.",
        },
        shot("macro", "Material close-up", "Texture, label, finish",
          "A macro close-up of the exact same product: the camera very close on its most beautiful detail (material, " +
            "surface, stitching, label or finish), soft raking light that shows the texture, shallow depth of field. " +
            "Shape, color, label and details stay identical."),
        shot("inHand", "In the hand", "Scale and touch",
          "The exact same product held naturally in one hand at a realistic size, soft daylight, a calm softly " +
            "blurred background that fits their customers. Only the hand and forearm, no face. Shape, color, label " +
            "and details stay identical."),
        {
          id: "lifestyle",
          label: "Lifestyle scene",
          light: "Where it is used",
          text:
            "Place the exact same product where their customers would really use it, in a realistic scene with " +
            "natural light, believable scale and real shadows. Product shape, color, label and details stay identical.",
        },
        shot("flatlay", "Flat lay", "From above, styled",
          "A top-down flat lay: the exact same product on a calm surface that fits the brand, with at most two " +
            "items that genuinely belong with it, even soft daylight, clean composition with space around. Shape, " +
            "color, label and details stay identical."),
        seasonal("e.g. a few autumn leaves or a winter fabric as backdrop"),
      ],
    },
    {
      id: "dish",
      guard: "No extra garnish, sauce, drizzle, steam or ingredients that are not on the original plate; same doneness, browning and portion size.",
      label: "Dish / Food",
      icon: "fa-utensils",
      aspect: "square",
      target: "a photo of a plated dish, food or drinks",
      pageHints: ["speisekarte", "menu", "karte", "restaurant", "essen", "food", "gerichte", "kulinarik", "frühstück", "breakfast"],
      exampleScenario: "restaurant",
      directions: [
        retouch(
          "Remove everything that distracts from the dish: used napkins, sauce bottles, half-empty glasses and crumbs. Clean plate edges, fresh and appetising food. Same dish and portion."
        ),
        shot("overhead", "From above", "Top-down, plated",
          "A new top-down shot straight from above: the same dish on its plate centered on their table wiped " +
            "clean, cutlery and a napkin placed neatly beside it, soft window light. Same dish, ingredients, portion " +
            "and plate."),
        shot("closeup", "Close-up", "Low angle, close",
          "A new close-up at a low angle, as a food photographer would shoot the hero bite: the camera close to the " +
            "plate, the texture and crust of the food sharp, the background softly out of focus, soft side light. " +
            "Same dish, ingredients, browning and portion; no added sauce, garnish or steam."),
        shot("served", "Being served", "Hands, real moment",
          "The same dish in the moment it is served: a waiter's hand setting the plate down on a cleanly set table " +
            "in their dining room, natural light, candid. Only the hand and sleeve, no faces. Same dish, plate and room."),
        {
          id: "moody",
          label: "Dark & moody",
          light: "Low warm light",
          text:
            "Give the same dish an evening restaurant mood: dark table, warm low side light that makes the food glow, " +
            "deep natural shadows, softly blurred warm lights in the background, no napkins, bottles or used glasses on " +
            "the table. Same dish, portion and plate.",
        },
        seasonal(
          "e.g. seasonal light and colors in the background or one fitting seasonal detail on the table; no herb sprigs or scattered leaves",
          "Remove used napkins, sauce bottles and half-empty glasses. "
        ),
      ],
    },
    {
      id: "room",
      guard: "Same bed size and number of beds, the same furniture and the same real view; condition and finishes stay as they are.",
      label: "Hotel / Holiday room",
      icon: "fa-bed",
      aspect: "landscape",
      target: "an interior photo of a hotel room, suite or holiday apartment with the bed visible",
      pageHints: ["zimmer", "rooms", "room", "suites", "suite", "unterkunft", "accommodation", "übernachten", "apartments", "ferienwohnung"],
      exampleScenario: "hotel",
      directions: [
        retouch("Make the bed smooth and neat and remove cables, bags and personal items."),
        shot("fromBed", "View from the bed", "The guest's view",
          "A new shot from the bed toward the window, as a guest would see it waking up: the end of the neatly made " +
            "bed in the foreground, the real view through the same window, soft morning light. Same room, window, " +
            "furniture and view."),
        shot("details", "Bed detail", "Linen and textures",
          "A new close-up of the made bed: crisp linen, pillows and the headboard, the real textures of the " +
            "fabrics, soft daylight from the window, the rest of the room softly out of focus. Same bed, linen colors " +
            "and headboard."),
        {
          id: "sunny",
          label: "Sunny day",
          light: "Sunshine, blue sky",
          text:
            "Show the same room on a clear sunny day: blue sky outside, soft sunlight falling in through the windows " +
            "with gentle shadows, bright and airy. Bed smooth and neat, remove cables, bags and personal items, ceiling " +
            "lamps off. Add nothing new.",
        },
        {
          id: "evening",
          label: "Evening glow",
          light: "Dusk, lamps on",
          text:
            "Show the same room at blue hour: deep blue sky outside, the existing lamps switched on and filling the " +
            "room with warm, inviting light, bright enough to see every detail. Bed smooth and neat, no clutter.",
        },
        seasonal("e.g. a knitted blanket in autumn and winter, fresh flowers in spring"),
      ],
    },
    {
      id: "exterior",
      guard: "Same number of floors, windows, balconies and signage; no added landscaping, terraces or pools.",
      label: "Building / Exterior",
      icon: "fa-building",
      exampleScenario: "facade",
      aspect: "landscape",
      target: "an outside photo of their building, facade or entrance",
      pageHints: ["haus", "hotel", "lage", "location", "anfahrt", "ueber-uns", "about", "standort"],
      directions: [
        retouch("Clean pavement and entrance, remove cars, bins and construction signs in front."),
        shot("entrance", "Welcome at the door", "Closer, door open",
          "A new, closer shot of their entrance at eye level: the door open, warm light inside, the entrance and " +
            "the real sign clean and inviting, soft daylight. Same door, sign, facade and materials."),
        {
          id: "sunny",
          label: "Sunny day",
          light: "Sunshine, blue sky",
          text:
            "Show the same building on a clear sunny day: blue sky, warm sunlight on the facade, clean pavement and " +
            "entrance. Building, facade and signage stay exactly the same.",
        },
        {
          id: "bluehour",
          label: "Blue hour",
          light: "Dusk, windows lit",
          text:
            "Show the same building at blue hour: deep blue evening sky and warm light glowing from the windows and " +
            "entrance, inviting. Building, facade and signage stay exactly the same.",
        },
        seasonal("e.g. autumn trees, a light layer of snow, spring blossoms"),
      ],
    },
    {
      id: "staging",
      guard: "Furniture stands on the floor with contact shadows, at a realistic scale for the room, never blocks windows, doors or radiators, and fits the building's era and the price level of the listing.",
      label: "Real estate: virtual staging",
      icon: "fa-couch",
      aspect: "landscape",
      target: "an interior photo of an empty or sparsely furnished room (apartment, house or office listing)",
      pageHints: ["immobilien", "objekte", "angebote", "wohnungen", "kaufen", "mieten", "listings", "properties", "exposé", "expose"],
      exampleScenario: "realEstate",
      directions: [
        {
          id: "modern",
          label: "Modern & warm",
          light: "Furnished, daylight",
          text:
            "Virtually stage the room in a modern, warm style that fits its size: sofa, rug, coffee table, a few " +
            "plants and soft daylight. Walls, windows, floor and ceiling stay exactly as they are.",
        },
        {
          id: "scandi",
          label: "Scandinavian",
          light: "Light and airy",
          text:
            "Virtually stage the room in a light Scandinavian style: light wood, white and beige textiles, a few " +
            "plants, airy daylight. Walls, windows, floor and ceiling stay exactly as they are.",
        },
        {
          id: "premium",
          label: "Premium",
          light: "Warm accents",
          text:
            "Virtually stage the room in a high-end style: designer furniture, natural stone and brass accents, warm " +
            "accent lighting next to daylight. Walls, windows, floor and ceiling stay exactly as they are.",
        },
        shot("lived", "Lived-in morning", "Life, not a showroom",
          "Virtually stage the room as a real home on a calm morning: furniture that fits its size, breakfast for " +
            "two set on the table, a throw on the sofa, low morning sun. No people. Walls, windows, floor and ceiling " +
            "stay exactly as they are."),
        retouch("Leave the room empty but make it look clean, bright and spacious."),
      ],
    },
    {
      id: "team",
      guard: "No beautification: same age, face shape, skin texture, teeth, hair and body; never add or remove people.",
      label: "Team / Owner",
      icon: "fa-user-tie",
      exampleScenario: "owner",
      aspect: "landscape",
      target: "a photo of the owner or the team (real people)",
      pageHints: ["team", "ueber-uns", "uber-uns", "über-uns", "about", "wir", "mitarbeiter"],
      directions: [
        retouch("Even, flattering light on the faces and a calmer background. No skin smoothing beyond what a photographer would do."),
        {
          id: "brand",
          label: "Brand background",
          light: "Studio light",
          text:
            "Keep the same people and poses but give them soft studio light and replace the room behind them with a " +
            "calm, slightly blurred background in one brand color. The original room is no longer visible. Faces, " +
            "bodies, expressions and clothing stay exactly the same.",
        },
        shot("portrait", "Portrait crop", "Closer, head and shoulders",
          "A closer head-and-shoulders portrait of the same people, as a photographer would crop it, with soft " +
            "window light on the faces and their own room softly out of focus behind. Faces, expressions and " +
            "clothing stay exactly the same."),
      ],
    },
    {
      id: "custom",
      label: "Custom photo",
      icon: "fa-pen",
      aspect: "landscape",
      target: "",
      pageHints: [],
      directions: [
        retouch("Remove distracting clutter."),
        shot("detail", "Close-up", "The best detail",
          "A new close-up of the most characteristic detail in the photo, soft side light, the background softly " +
            "out of focus. Same real thing, nothing invented."),
        seasonal("small seasonal details that fit the business"),
      ],
    },
  ].map((type) => ({ ...type, brief: type.directions[0].text })),
  exampleBase: "/img/sales-studio/photo-upgrade",
  exampleBefore: true,
  examples: {
    scenarios: [
      { id: "hotel", label: "Hotel room", type: "room", engineDirection: "natural" },
      { id: "restaurant", label: "Restaurant dish", type: "dish", engineDirection: "natural" },
      { id: "realEstate", label: "Real estate listing", type: "staging", engineDirection: "modern" },
    ],
  },
  styles: [],
  frames: [
    { id: "beforeAfter", label: "Before / after side by side", prompt: "Show the ORIGINAL photo on the left and the upgraded version on the right, side by side, with small 'Before' and 'After' labels." },
    { id: "after", label: "Upgraded photo only", prompt: "Show only the upgraded photo, edge-to-edge." },
  ],
  buildPrompt: ({ label, target, brief, frame, guard }) => [
    `Professionally upgrade the attached real photo from {{business_name}}'s website (${label}).`,
    `It was picked from their website because it shows ${target}. It must stay unmistakably the same real place, product or people.`,
    `What to do: ${brief}`,
    /season/i.test(brief) ? "The current season is {{current_season}}." : "",
    ...PHOTO_RULES,
    guard ? `For this kind of photo: ${guard}` : "",
    "Do not add text, logos or watermarks that are not in the original.",
    `Presentation: ${frame.prompt}`,
  ],
  altText: (label) => `${label} photo upgrade for {{business_name}}`,
};

// Figures the image may compute, and only these. Anything else must be copied
// from the recipient context or left off the graphic.
const ANALYSIS_MATH =
  "Calculate only with these rules, and print the formula in small type under each result: " +
  "a yearly figure is the monthly figure × 12; " +
  "a change in money is the base amount × (the percent ÷ 100); " +
  "a share is the part ÷ the total, shown as a percent with one decimal. " +
  "Use the exact inputs from the recipient context. Never invent, estimate or quietly round a number that is not produced by these rules. " +
  "If an input the formula needs is missing, omit that result and write \"not in the data\" instead.";

const analysis = (id, label, light, calculates, text) => ({ id, label, light, calculates, text });

const ANALYSES = {
  id: "analyses",
  name: "Analysis Designer",
  defaultName: "Analysis Designer",
  defaultPrefix: "analysis",
  itemNoun: "Analysis",
  itemNounPlural: "Analyses",
  itemsHint: "Pick a graphic. It is drawn only from the data already stored on the contact. Turn on calculation when a figure has to be worked out first.",
  maxItems: 3,
  smartPieces: true,
  lookShots: true,
  sourceMode: "prompt",
  outputSub: "Name, tag, and a live test of the email for a real contact.",
  demoText: "I turned the numbers we already have on you into one graphic:",
  smartCopy: {
    ideaKicker: "The graphic",
    ideaQuestion: "Which numbers?",
    promptLabel: "Prompt",
    promptNote: "from the template, edit it freely",
    hint: "Only numbers that are already on the contact are drawn. Nothing is estimated.",
  },
  calculation: {
    title: "Calculate the figures first",
    note: "The result is worked out from their data before the graphic is drawn, with the formula on it. A model that is weak with numbers is switched to a stronger one.",
    hint: "Every result is monthly × 12, a percent of a known amount, or a share of a known total. Missing inputs stay off the graphic.",
    engineHint: "A figure has to be calculated, so text accuracy matters most. Nano Banana Pro sets the finished numbers in type.",
  },
  calculationModel: "gemini-3-pro-image",
  perItemAspect: true,
  defaultAspect: "landscape",
  defaultModel: "gemini-3-pro-image",
  modelFocus: ["text", "brand"],
  modelFocusLabel: "reading their numbers and setting them in type",
  defaultItems: ["analysis"],
  defaultDirection: "",
  directionPlaceholder: "",
  itemTypes: [
    {
      id: "analysis",
      label: "Analysis",
      icon: "fa-chart-line",
      aspect: "landscape",
      directions: [
        analysis("visibility", "Visibility over time", "The points you have", false,
          "A clean line chart of their SEO visibility over time, designed like a slide from a premium pitch deck. "
          + "Plot every \"Visibility over time\" data point from the context as a dot on one smooth line, oldest on the left, newest on the right, and write the exact value above each dot. Month labels on the x-axis exactly as given; a light y-axis with three or four quiet gridlines starting near the lowest value. "
          + "Top left: the title \"Sichtbarkeit\" set large, under it their name small and the trend (up, down or flat) as one short line, with the percent change only if it is in the context. "
          + "Highlight the newest point with a larger dot and its value in bold. No point, month or value that is not in the data, no second line, no forecast."),
        analysis("competitor", "You vs. competitor", "Overlap, not a guess", false,
          "A bar chart titled \"Wo ihr steht\", set large at the top left, laid out like a clean pitch-deck slide. "
          + "Two horizontal bars with plenty of space between them. One bar is {{business_name}} with their visibility index, in the accent color. The other is {{seo_main_competitor}} with the keyword-overlap score written next to their name in the context, in a neutral grey. "
          + "Those are different measures, so put a small label under each bar that says what the number is (visibility index, keyword overlap), and set the number itself large at the end of the bar. "
          + "If a number is not written down, print the name without a bar. No third bar, no ranking claim, no invented winner."),
        analysis("traffic", "Traffic value", "What a month is worth", false,
          "One calm key-figure slide titled \"Organischer Traffic\". Two large figures side by side, separated by a thin vertical rule: on the left the estimated organic visitors per month with the label \"Besucher / Monat\", on the right what that traffic is worth per month with the label \"Wert / Monat\". "
          + "Both numbers taken word for word from the context, set very large in a clean font, with the labels small above them. "
          + "One short line at the bottom that says these are monthly estimates. No yearly total, no projection, no chart that suggests a trend."),
        analysis("keywords", "Keyword opportunities", "The quick wins", false,
          "A keyword-opportunity slide titled \"Keyword-Chancen\". Take the keyword opportunities from the context: the best one, the one with the best position, sits large in the middle inside a circle or card, with its position written underneath as \"Position X\". "
          + "Up to two more keywords sit smaller on either side, each with its own position, connected visually to the center. "
          + "Keywords in quotation marks, exactly as written, in lower case if they are written that way. One short line at the bottom such as \"Kurz vor Seite 1\" only if the positions are between 4 and 15. No keyword or position that is not listed."),
        analysis("roi", "What the trend is worth", "Monthly × 12, and the swing", true,
          "Show what their visibility trend is worth in money, as a clear four-figure pitch-deck slide titled \"Was die Entwicklung wert ist\". "
          + "Monthly organic value is the traffic value from the context. Yearly value is that monthly value × 12. The monthly swing is the monthly value × (the visibility change percent ÷ 100), and the yearly swing is the monthly swing × 12. "
          + "Lay them out as four columns or a two-by-two grid: small label on top, the result set large, its formula in small type underneath. "
          + "A gain is shown with a plus sign and an upward arrow in the accent color, a loss with a minus sign and a downward arrow. Show each result with its formula, and never put a formula under the wrong figure."),
        analysis("share", "Share in the top 10", "Part ÷ total", true,
          "Show how much of their keyword set sits in the top 10, as a calm pitch-deck slide titled \"Anteil in den Top 10\". "
          + "The share is keywords in the top 10 ÷ keywords total, as a percent with one decimal. "
          + "On the left a large ring or donut chart whose filled part matches the share, with the percent set large in its center. On the right the two inputs as labeled figures (\"Keywords in den Top 10\", \"Keywords gesamt\") and below them the formula in small type. "
          + "Show the formula. No other figure."),
        analysis("reviews", "Reviews at a glance", "Score and count", false,
          "Their review score and review count as one calm, confident slide titled \"Bewertungen\". "
          + "The score from {{lead_rating}} set very large in the center, with \"von 5\" small beside it, and a single row of five star outlines filled to match the score exactly. Under it {{lead_reviews}} followed by \"Bewertungen\". "
          + "Use {{lead_rating}} and {{lead_reviews}} only. No stars beyond the score, no invented quote, no platform logo."),
      ],
    },
  ],
  exampleBase: "/img/sales-studio/analyses",
  exampleBefore: true,
  exampleRatio: "3 / 2",
  exampleNote: "data set. Before is the raw numbers the engine got, after is what it designed. Check who keeps every figure exact.",
  examples: {
    scenarios: [
      {
        id: "hotel",
        label: "Visibility chart",
        type: "analysis",
        engineDirection: "visibility",
        icon: "fa-chart-line",
        note: "data set, with Berghof Alpin's logo and brand colors attached. Before is what the engine got. After is the pitch slide. Check who keeps the mark, the palette and every figure.",
      },
      {
        id: "restaurant",
        label: "ROI slide",
        type: "analysis",
        engineDirection: "roi",
        icon: "fa-calculator",
        note: "data set, with Gasthaus Linde's logo and brand colors attached. Before is what the engine got. After is their yearly ledger. Check who keeps the mark, shows the loss in burgundy, and puts the right formula under each figure.",
      },
      {
        id: "realEstate",
        label: "Keyword ranking",
        type: "analysis",
        engineDirection: "keywords",
        icon: "fa-chart-bar",
        note: "data set, with Atelier Nord's logo and brand colors attached. Before is what the engine got. After is a floor plan of their keywords. Check who keeps the mark, the coral only on the best keyword, and every word exact.",
      },
    ],
  },
  styles: [],
  frames: [
    { id: "chart", label: "The chart", prompt: "Render only the graphic, edge to edge, on white. No device and no mockup." },
  ],
  buildPrompt: ({ label, brief, frame, calculate }) => [
    `Design an analysis graphic for {{business_name}} (${label}).`,
    "Read the recipient context first. Every number on the graphic is either copied from that context or produced by a formula that uses only those numbers.",
    `What to show: ${brief}`,
    calculate ? ANALYSIS_MATH : "Do not calculate a new figure. If a number is not written in the context, leave it off the graphic.",
    "Design quality of a premium pitch-deck slide: a strict grid, generous margins, one clear title, large crisp figures and small quiet labels, refined modern typography, perfectly aligned. "
      + "White background, one petrol accent plus neutral greys, flat and sharp, no gradients, no 3D, no clip art, no stock photos, no fake logos. Labels in the language of their website.",
    `Presentation: ${frame.prompt}`,
  ],
  altText: (label) => `${label} for {{business_name}}`,
};

// Each idea is a gag that adapts to what the business does. The hero is always
// worked out from the recipient context, so a bakery gets a croissant and a
// dentist gets a tooth, from the same template.
const gag = (id, label, light, text, shot) => ({ id, label, light, text, ...(shot ? { shot } : {}) });

const FUNNY_GIF = {
  id: "funnyGif",
  name: "Funny GIF",
  defaultName: "Funny GIF",
  defaultPrefix: "gif",
  itemNoun: "GIF",
  itemNounPlural: "GIFs",
  itemsHint: "Pick a gag. It is cast with what this business actually does, read from their website and research.",
  maxItems: 2,
  smartPieces: true,
  lookShots: true,
  engines: "gif",
  exampleExt: "gif",
  exampleRatio: "1 / 1",
  directionIcon: "fa-solid fa-film",
  maxWidth: 300,
  // Display size in the email. The GIF file stays ~300 px; this only sets the <img> width.
  displayWidth: { min: 140, max: 480, step: 10 },
  defaultAlign: "left",
  cast: true,
  pinnable: true,
  sourceMode: "prompt",
  outputSub: "Name, tag, and a live test of the email for a real contact.",
  demoText: "Made you something, it says more than a long email:",
  smartCopy: {
    ideaKicker: "The gag",
    ideaQuestion: "What happens in 4 seconds?",
    promptLabel: "Prompt",
    promptNote: "from the template, or write your own gag",
    hint: "4 seconds, looping, no text. The star is whatever they sell or do.",
  },
  perItemAspect: true,
  defaultAspect: "square",
  defaultModel: "heygen-video-1",
  modelFocus: ["fun", "prompt"],
  modelFocusLabel: "comic timing and sticking to the gag",
  engineHint: "Every GIF is 4 seconds of 480p video, turned into a small looping GIF that plays inline in the email.",
  defaultItems: ["gif"],
  defaultDirection: "",
  directionPlaceholder: "",
  itemTypes: [
    {
      id: "gif",
      label: "GIF",
      icon: "fa-face-laugh-squint",
      aspect: "square",
      directions: [
        gag("slowmo", "Slow-mo entrance", "Movie trailer",
          "Movie trailer parody in glossy cinematic 3D animation. The sender of this email strides in slow motion through the front door of their business, sunglasses on, coat flowing, wind in the hair, and hands over their signature product like a top secret briefcase. Dramatic backlight, a few sparks, completely over the top. Full frame, no black bars.",
          "/img/sales-studio/gif/cast/slowmo.gif"),
        gag("micdrop", "Mic drop", "Stand-up",
          "Stand-up comedy stage in stylised 3D animation, one spotlight. The sender of this email finishes a pitch next to their signature product, which has a cartoon face, drops the microphone with a cool shrug, and the product bursts into a standing ovation. Warm club light, a brick wall behind.",
          "/img/sales-studio/gif/cast/micdrop.gif"),
        gag("deal", "Done deal", "Claymation",
          "Claymation in a tiny clay office. The sender of this email and their signature product, as a clay character with a face, sit across a desk, lean in seriously, then shake hands. The handshake turns into an over the top fist bump with a puff of clay confetti. Handmade stop-motion look.",
          "/img/sales-studio/gif/cast/deal.gif"),
        gag("spotter", "Gym spotter", "Sports",
          "A gym in bold stylised 3D animation. Their signature product, with a cartoon face, struggles under a heavy barbell on a bench. The sender of this email spots it like a coach, shouting encouragement, and the product pushes the bar up with a mighty roar. They high five, both dripping with sweat. Punchy gym light.",
          "/img/sales-studio/gif/cast/spotter.gif"),
        gag("mascot", "Mascot happy dance", "Cartoon",
          "Their signature product or service turns into a cartoon mascot with a face and tiny arms. It pops into frame, does a proud little happy dance and ends with a big thumbs up. Bold flat cartoon, bright colors, plain background."),
        gag("rocket", "Liftoff", "Cartoon",
          "Their signature product sits on a tiny launch pad, wobbles, then blasts off like a rocket with a puff of cartoon smoke and zooms up out of frame. Bold flat cartoon, simple sky background."),
        gag("rush", "The big rush", "Claymation",
          "A claymation miniature of their shop or workplace. Tiny clay customers flood in all at once and one tiny clay owner handles everyone at hyper speed, then wipes their brow, relieved and proud. Warm, handmade claymation look."),
        gag("hero", "Epic slow motion", "Movie parody",
          "An over the top slow-motion hero shot of their signature product, like a movie trailer: dramatic light, flying crumbs or sparks or water drops, a slow camera push. Comically epic, cinematic, one product only."),
        gag("cat", "Office cat approves", "Real life",
          "A fluffy cat sits next to a laptop in their kind of workplace. The laptop screen suddenly glows brighter, the cat's eyes go wide, it leans in close, then sits back looking very pleased with itself. Natural light, real-life video look, funny reaction."),
        gag("fail", "Oops, then wow", "Cartoon",
          "Something from their daily work goes comically wrong for a second, a wobble, a spill or a tumble, then snaps into a perfect result with a little sparkle. Bold flat cartoon, clear before and after in one move."),
        gag("toy", "Toy world", "3D toy",
          "Their shop or product as a tiny glossy 3D toy set on a desk. A small toy figure walks up, opens the door or picks up the product, and does a happy jump. Soft studio light, playful miniature look."),
        gag("highfive", "Inbox high five", "Cartoon",
          "A cartoon envelope with a face flies in and gives a big high five to their signature product, also with a face. Both bounce with joy, little stars pop. Bold flat cartoon, plain bright background."),
        gag("booked", "Fully booked", "Cartoon",
          "Their own kind of capacity fills up one spot at a time with a happy pop: tables in a restaurant, rooms in a hotel, chairs in a salon or practice, bikes on a repair stand. The last spot fills, and their signature product with a cartoon face faints backwards with joy. Bold flat cartoon, bright colors."),
        gag("hometown", "Hometown hero", "3D toy",
          "Their signature product as a giant friendly hero strolls through a tiny toy version of their home town, with the landscape and buildings their region is known for. Tiny locals wave and cheer, the hero waves back carefully. Soft daylight, playful miniature look."),
        gag("race", "Pulling ahead", "Cartoon",
          "Two lookalike versions of their signature product race neck and neck on a little track. Theirs winks, fires up a cartoon turbo and pulls ahead to win by a nose, then celebrates with a victory jump. The rival is plain and generic. Bold flat cartoon, speed lines."),
        gag("reply", "Hit reply", "Cartoon",
          "Their signature product with a cartoon face stands next to a big shiny round button with a curved reply arrow symbol on it. It looks at the button, looks at the camera, hesitates, then smashes the button with both hands and confetti bursts everywhere. Bold flat cartoon, plain bright background."),
        gag("delivery", "Special delivery", "Cartoon",
          "A friendly cartoon person, the sender of this email, walks up to their front door holding a big gift box with a bow. The door opens, the box pops open and their signature product jumps out with a happy cheer. The person takes a little bow. Bold flat cartoon, bright colors."),
        gag("fan", "Biggest fan", "Cartoon",
          "A friendly cartoon person, the sender of this email, is starstruck in front of their signature product, which is twice their size, has a cartoon face and poses like a celebrity. The person holds up a phone for a selfie, the product leans in and winks, the flash goes off and both grin. Bold flat cartoon, little sparkles."),
      ],
    },
  ],
  exampleBase: "/img/sales-studio/gif",
  exampleNote: "gag, so you can see who gets the comic timing right.",
  examples: {
    scenarios: [
      { id: "bakery", label: "Bakery", type: "gif", engineDirection: "mascot", icon: "fa-bread-slice" },
      { id: "dentist", label: "Dentist", type: "gif", engineDirection: "rocket", icon: "fa-tooth" },
      { id: "hotel", label: "Hotel", type: "gif", engineDirection: "rush", icon: "fa-hotel" },
    ],
  },
  styles: [],
  // The look is chosen apart from the gag, so a story written as a cartoon can
  // still be rendered realistic. "gag" keeps whatever style the gag text names.
  looks: [
    { id: "gag", label: "As the gag", icon: "fa-masks-theater", person: "cartoon", crowd: "a cartoon character" },
    {
      id: "cartoon",
      label: "Cartoon",
      icon: "fa-pen-nib",
      person: "cartoon",
      crowd: "a cartoon character",
      prompt: "Bold flat 2D cartoon: clean outlines, bright solid colours, simple shapes, playful squash and stretch.",
    },
    {
      id: "real",
      label: "Realistic",
      icon: "fa-camera",
      person: "photorealistic",
      crowd: "a real-looking person",
      prompt:
        "Photorealistic live-action video: real materials, natural light, a handheld camera feel, like a well shot social clip. Objects that act stay real objects, with no cartoon faces, and get their personality from how they move.",
    },
    {
      id: "cinema",
      label: "Cinematic",
      icon: "fa-film",
      person: "cinematic, true to life",
      crowd: "a true-to-life film character",
      prompt:
        "Cinematic film look: anamorphic lens, shallow depth of field, dramatic backlight, rich colour grade, slow confident camera moves. Played dead serious, which makes the gag funnier.",
    },
    {
      id: "clay",
      label: "Claymation",
      icon: "fa-hand-sparkles",
      person: "clay",
      crowd: "a clay character",
      prompt: "Handmade claymation stop-motion: visible fingerprints in the clay, miniature set, soft studio light, slightly choppy charming motion.",
    },
  ],
  frames: [{ id: "gif", label: "The GIF", prompt: "Fill the frame. No device, no mockup, no border." }],
  buildPrompt: ({ brief, cast = {}, look }) => [
    "A short, silent, funny animated clip of 4 seconds.",
    "The star is what this business sells or does, taken from the casting note. It has to be recognisable at a glance.",
    `What happens: ${brief}`,
    look?.prompt && `Look: ${look.prompt} This look replaces any other style named above.`,
    cast.me &&
      `The character from the sender's photo plays along, kept exactly as in that photo (same face, body, colours and outfit), a visitor in their own look, not staff of the business. They cheer, high five, hand something over or react to the star. Everyone else stays ${look?.crowd || "a cartoon character"}.`,
    cast.product && "The sender's own product appears as a prop, recognisable by its shape and colours.",
    cast.brand && "Dress the scene in the recipient's brand colours and put their logo on the star or a sign.",
    "One continuous shot, one camera, no comic panels and no split screen. One clear gag with a beginning, a punchline and an end that comes back close to the first frame, so it loops. Big simple shapes and strong motion that still read at 300 pixels.",
    cast.brand
      ? "No text anywhere except their logo: no captions, no subtitles, no other signs or letters, no watermarks. Friendly and harmless, never mocking the business."
      : "No text anywhere: no captions, no subtitles, no signs, no letters, no logos, no watermarks. Friendly and harmless, never mocking the business.",
    cast.me
      ? "Keep the referenced character exactly as in their photo."
      : "No real people's faces.",
  ],
  altText: (label) => `${label} for {{business_name}}`,
};

export const SALES_PLAY_CONFIGS = {
  [WEBSITE_REDESIGN.id]: WEBSITE_REDESIGN,
  [CONTENT_PIECES.id]: CONTENT_PIECES,
  [PHOTO_UPGRADE.id]: PHOTO_UPGRADE,
  [ANALYSES.id]: ANALYSES,
  [FUNNY_GIF.id]: FUNNY_GIF,
};
